import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
    Container,
    Box,
    Alert,
    CircularProgress,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Grid,
    Checkbox,
    FormControlLabel,
    TextField,
    Typography,
    Button,
} from '@mui/material';
import {
    LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import dayjs from 'dayjs';
import Header from './Header';
import api from '../api/api';
import { MEASURE_TYPES, getMeasureColors, getMeasureUnits, getMeasureLabels, MEASURE_CONFIG } from '../utils/measurementConfig';
import 'dayjs/plugin/utc';
import 'dayjs/plugin/timezone';

function GraphPage() {
    const { t } = useTranslation();
    const { zone: initialZoneParam } = useParams();

    const [sensorData, setSensorData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [availableZones, setAvailableZones] = useState([]);

    const [selectedZone, setSelectedZone] = useState(initialZoneParam === 'null' ? '' : initialZoneParam || '');
    const [timeRange, setTimeRange] = useState('last24Hours');
    const [startDate, setStartDate] = useState(dayjs().subtract(24, 'hour'));
    const [endDate, setEndDate] = useState(dayjs());

    const [checkedMeasures, setCheckedMeasures] = useState(
        Object.fromEntries(MEASURE_TYPES.map(type => [type, true]))
    );

    const measureColors = getMeasureColors();
    const measureLabels = getMeasureLabels(t);
    const measureUnits = getMeasureUnits();

    const fetchAvailableZones = useCallback(async () => {
        try {
            const response = await api.get('/sensor/latest');
            const zones = [...new Set(response.data.map(item => item.zone))].filter(Boolean).sort();
            setAvailableZones(zones);
            if (zones.length > 0 && !initialZoneParam) {
                setSelectedZone(zones[0]);
            }
        } catch (err) {
            console.error('Failed to fetch available zones:', err);
        }
    }, [initialZoneParam]);

    useEffect(() => {
        fetchAvailableZones();
    }, [fetchAvailableZones]);

    const fetchDataForGraph = useCallback(async () => {
        if (!selectedZone) {
            setSensorData([]);
            setLoading(false);
            return;
        }

        setError('');
        setLoading(true);

        let start = startDate;
        let end = endDate;

        if (timeRange !== 'custom') {
            end = dayjs();
            switch (timeRange) {
                case 'last5Min': start = dayjs().subtract(5, 'minute'); break;
                case 'last30Min': start = dayjs().subtract(30, 'minute'); break;
                case 'last1Hour': start = dayjs().subtract(1, 'hour'); break;
                case 'last3Hours': start = dayjs().subtract(3, 'hour'); break;
                case 'last6Hour': start = dayjs().subtract(6, 'hour'); break;
                case 'last12Hours': start = dayjs().subtract(12, 'hour'); break;
                case 'last24Hours': start = dayjs().subtract(24, 'hour'); break;
                default: break;
            }
        }

        try {
            const response = await api.post('/search-sensor', {
                zone: selectedZone === '' ? null : selectedZone,
                startDate: start.format(),
                endDate: end.format(),
            });

            const processedData = response.data.flatMap(feed =>
                feed.measures.map(measure => ({
                    creationDate: feed.creationDate,
                    type: measure.type,
                    value: parseFloat(measure.value),
                }))
            );

            const groupedData = processedData.reduce((acc, item) => {
                const date = item.creationDate;
                if (!acc[date]) {
                    acc[date] = { creationDate: date };
                }
                acc[date][item.type] = item.value;
                return acc;
            }, {});

            const chartData = Object.values(groupedData).sort((a, b) =>
                dayjs(a.creationDate).valueOf() - dayjs(b.creationDate).valueOf()
            );

            setSensorData(chartData);
            setError('');
        } catch (err) {
            setError(t('common.errorOccurred', { message: err.response?.data?.message || err.message }));
            setSensorData([]);
        } finally {
            setLoading(false);
        }
    }, [selectedZone, timeRange, startDate, endDate, t]);

    useEffect(() => {
        fetchDataForGraph();
    }, [fetchDataForGraph, selectedZone, timeRange, startDate, endDate]);

    const handleZoneChange = (event) => {
        setSelectedZone(event.target.value);
    };

    const handleTimeRangeChange = (event) => {
        setTimeRange(event.target.value);
    };

    const handleMeasureToggle = (type) => {
        setCheckedMeasures((prev) => ({ ...prev, [type]: !prev[type] }));
    };

    const formatXAxis = (tickItem) => {
        return dayjs(tickItem).format('HH:mm');
    };

    // Calculate min/max for each measure
    const dynamicDomains = useMemo(() => {
        const domains = {};
        const allMeasures = Object.keys(checkedMeasures);

        allMeasures.forEach(measureType => {
            const values = sensorData
                .filter(d => d[measureType] !== undefined)
                .map(d => d[measureType]);

            if (values.length > 0) {
                const min = Math.min(...values);
                const max = Math.max(...values);
                const buffer = (max - min) * 0.1;

                let domainMin;
                let domainMax;

                switch (measureType) {
                    case 'POWE':
                        domainMin = Math.max(0, min - buffer); // Ensure min is not less than 0
                        domainMax = Math.ceil(max + buffer); // Round max up to the nearest integer
                        break;
                    case 'TEMP':
                    case 'HUMI':
                        domainMin = Math.floor(min - buffer); // Round min down to the nearest integer
                        domainMax = Math.ceil(max + buffer); // Round max up to the nearest integer
                        break;
                    case 'PRES':
                        // Format to two decimal places, then convert back to a number
                        domainMin = parseFloat((min - buffer).toFixed(2));
                        domainMax = parseFloat((max + buffer).toFixed(2));
                        break;
                    default:
                        domainMin = min - buffer;
                        domainMax = max + buffer;
                }

                domains[measureType] = {
                    min: domainMin,
                    max: domainMax,
                    hasData: true,
                };
            } else {
                domains[measureType] = { hasData: false };
            }
        });
        return domains;
    }, [sensorData, checkedMeasures]);

    // Check if any line is checked and has data for the legend
    const hasLegendData = useMemo(() => {
        return Object.keys(checkedMeasures).some(type => checkedMeasures[type] && dynamicDomains[type]?.hasData);
    }, [checkedMeasures, dynamicDomains]);

    return (
        <Box sx={{ flexGrow: 1 }}>
            <Header title={t('common.graph')} />

            <Container sx={{ mt: 4 }}>
                {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

                <Grid container spacing={2} alignItems="center" sx={{ mb: 3 }}>
                    <Grid item xs={12} sm={4}>
                        <FormControl fullWidth>
                            <InputLabel id="zone-select-label">{t('common.selectZone')}</InputLabel>
                            <Select
                                labelId="zone-select-label"
                                id="zone-select"
                                value={selectedZone}
                                label={t('common.selectZone')}
                                onChange={handleZoneChange}
                            >
                                <MenuItem value=""><em>{t('common.selectZone')}</em></MenuItem>
                                {availableZones.map((zoneOption) => (
                                    <MenuItem key={zoneOption || 'null'} value={zoneOption}>
                                        {zoneOption || t('common.unassignedZone')}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <FormControl fullWidth>
                            <InputLabel id="time-range-select-label">{t('common.selectTimeRange')}</InputLabel>
                            <Select
                                labelId="time-range-select-label"
                                id="time-range-select"
                                value={timeRange}
                                label={t('common.selectTimeRange')}
                                onChange={handleTimeRangeChange}
                            >
                                <MenuItem value="last5Min">{t('common.last5Min')}</MenuItem>
                                <MenuItem value="last30Min">{t('common.last30Min')}</MenuItem>
                                <MenuItem value="last1Hour">{t('common.last1Hour')}</MenuItem>
                                <MenuItem value="last3Hours">{t('common.last3Hours')}</MenuItem>
                                <MenuItem value="last6Hour">{t('common.last6Hour')}</MenuItem>
                                <MenuItem value="last12Hours">{t('common.last12Hours')}</MenuItem>
                                <MenuItem value="last24Hours">{t('common.last24Hours')}</MenuItem>
                                <MenuItem value="custom">{t('common.customRange')}</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <Button variant="contained" onClick={fetchDataForGraph} disabled={loading || !selectedZone}>
                            {t('common.search')}
                        </Button>
                    </Grid>

                    {timeRange === 'custom' && (
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <Grid item xs={12} sm={6}>
                                <DateTimePicker
                                    label={t('common.startDate')}
                                    value={startDate}
                                    onChange={(newValue) => setStartDate(newValue)}
                                    renderInput={(params) => <TextField {...params} fullWidth />}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <DateTimePicker
                                    label={t('common.endDate')}
                                    value={endDate}
                                    onChange={(newValue) => setEndDate(newValue)}
                                    renderInput={(params) => <TextField {...params} fullWidth />}
                                />
                            </Grid>
                        </LocalizationProvider>
                    )}
                </Grid>

                <Box sx={{ mb: 2 }}>
                    {Object.keys(checkedMeasures).map((type) => (
                        // Conditionally render the FormControlLabel
                        dynamicDomains[type]?.hasData && (
                            <FormControlLabel
                                key={type}
                                control={
                                    <Checkbox
                                        checked={checkedMeasures[type]}
                                        onChange={() => handleMeasureToggle(type)}
                                        name={type}
                                        sx={{ color: measureColors[type] }}
                                    />
                                }
                                label={measureLabels[type]}
                            />
                        )
                    ))}
                </Box>

                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                        <CircularProgress />
                    </Box>
                ) : sensorData.length > 0 ? (
                    <ResponsiveContainer width="100%" height={400}>
                        <LineChart
                            data={sensorData}
                            margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                        >
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="creationDate" tickFormatter={formatXAxis} />

                            {/* --- CONDITIONAL YAXIS RENDERING --- */}
                            {checkedMeasures.TEMP && dynamicDomains.TEMP?.hasData && (
                                <YAxis
                                    yAxisId="tempAxis"
                                    orientation="left"
                                    domain={[dynamicDomains.TEMP.min, dynamicDomains.TEMP.max]}
                                />
                            )}
                            {checkedMeasures.HUMI && dynamicDomains.HUMI?.hasData && (
                                <YAxis
                                    yAxisId="humiAxis"
                                    orientation="left"
                                    domain={[dynamicDomains.HUMI.min, dynamicDomains.HUMI.max]}
                                />
                            )}
                            {checkedMeasures.POWE && dynamicDomains.POWE?.hasData && (
                                <YAxis
                                    yAxisId="powerAxis"
                                    orientation="right"
                                    domain={[dynamicDomains.POWE.min, dynamicDomains.POWE.max]}
                                />
                            )}
                            {checkedMeasures.PRES && dynamicDomains.PRES?.hasData && (
                                <YAxis
                                    yAxisId="presAxis"
                                    orientation="right"
                                    domain={[dynamicDomains.PRES.min, dynamicDomains.PRES.max]}
                                />
                            )}
                            <Tooltip labelFormatter={(label) => dayjs(label).format('YYYY/MM/DD HH:mm:ss')} />

                            {/* --- CONDITIONAL LEGEND RENDERING --- */}
                            {hasLegendData && <Legend />}

                            {/* --- CONDITIONAL LINE RENDERING --- */}
                            {checkedMeasures.TEMP && (
                                <Line
                                    yAxisId="tempAxis"
                                    type="monotone"
                                    dataKey="TEMP"
                                    stroke={measureColors.TEMP}
                                    activeDot={{ r: 8 }}
                                    name={measureLabels.TEMP}
                                    unit={measureUnits.TEMP}
                                />
                            )}
                            {checkedMeasures.HUMI && (
                                <Line
                                    yAxisId="humiAxis"
                                    type="monotone"
                                    dataKey="HUMI"
                                    stroke={measureColors.HUMI}
                                    activeDot={{ r: 8 }}
                                    name={measureLabels.HUMI}
                                    unit={measureUnits.HUMI}
                                />
                            )}
                            {checkedMeasures.POWE && (
                                <Line
                                    yAxisId="powerAxis"
                                    type="monotone"
                                    dataKey="POWE"
                                    stroke={measureColors.POWE}
                                    activeDot={{ r: 8 }}
                                    name={measureLabels.POWE}
                                    unit={measureUnits.POWE}
                                />
                            )}
                            {checkedMeasures.PRES && (
                                <Line
                                    yAxisId="presAxis"
                                    type="monotone"
                                    dataKey="PRES"
                                    stroke={measureColors.PRES}
                                    activeDot={{ r: 8 }}
                                    name={measureLabels.PRES}
                                    unit={measureUnits.PRES}
                                />
                            )}
                        </LineChart>
                    </ResponsiveContainer>
                ) : (
                    <Typography variant="h6" align="center" sx={{ mt: 4 }}>
                        {t('common.noDataForCriteria')}
                    </Typography>
                )}
            </Container>
        </Box>
    );
}

export default GraphPage;